import { BreakpointObserver } from '@angular/cdk/layout';
import { MatIconButton } from '@angular/material/button';
import { map } from 'rxjs';
import { constants } from '../../../environments/constants';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  inject,
  signal,
  effect,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BackendRole } from '../../authentication/backend-role';
import { AsyncPipe } from '@angular/common';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import {
  MatListItem,
  MatListItemIcon,
  MatListItemTitle,
  MatNavList,
} from '@angular/material/list';
import {
  ActivatedRoute,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { RoleService } from '../../authentication/role.service';
import {
  MatSidenav,
  MatSidenavContainer,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { isScreenWithoutMenus } from '../menu-visibility-helper';
import { InstallPwaComponent } from './install-pwa/install-pwa.component';
import { VersionInfoComponent } from './version-info/version-info.component';
import { FeatureConfigurationDataService } from '../../services/data/feature-configuration-data.service';
import { TrackedActionDirective } from 'shared/core';
import { environment } from '../../../environments/environment';
import { isDebugEnabled } from '../../debug/debug-route';

import {
  workflowAnswersEnabled,
  workflowOverviewEnabled,
} from '../../workflows/statistics/workflow-statistics-access';

type SideNavSection = 'calls' | 'workflows' | 'customers' | 'settings';

@Component({
  selector: 'app-side-nav',
  imports: [
    AsyncPipe,
    MatDivider,
    MatIcon,
    MatIconButton,
    MatListItem,
    MatListItemIcon,
    MatSidenavContent,
    RouterOutlet,
    MatNavList,
    MatSidenavContainer,
    MatSidenav,
    RouterLinkActive,
    RouterLink,
    InstallPwaComponent,
    MatListItemTitle,
    VersionInfoComponent,
    TrackedActionDirective,
  ],
  templateUrl: './side-nav.component.html',
  styleUrl: './side-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavComponent {
  @Output() activatedComponentChanged = new EventEmitter<unknown>();
  showSideNav = false;
  readonly compactNavigationOpen = signal(false);
  private compactNavigationTrigger: HTMLElement | null = null;
  protected readonly compactViewport = toSignal(
    inject(BreakpointObserver)
      .observe(`(max-width: ${constants.dimension.$lg}px)`)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );
  protected readonly workflowOverviewEnabled = workflowOverviewEnabled;
  protected readonly workflowAnswersEnabled = workflowAnswersEnabled;
  protected readonly BackendRole = BackendRole;
  protected readonly debugEnabled = isDebugEnabled(environment);

  protected readonly featureConfiguration = toSignal(
    inject(FeatureConfigurationDataService).featureConfiguration$,
  );
  protected readonly expandedSection = signal<SideNavSection | null>(null);

  constructor(
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    protected readonly roleService: RoleService,
  ) {
    effect(() => {
      if (!this.compactViewport()) {
        this.compactNavigationOpen.set(false);
      }
    });
  }

  toggleCompactNavigation(event?: MouseEvent): void {
    if (this.showSideNav && this.compactViewport()) {
      if (event?.currentTarget instanceof HTMLElement) {
        this.compactNavigationTrigger = event.currentTarget;
      }
      this.compactNavigationOpen.update((opened) => !opened);
    }
  }

  protected onCompactNavigationClosed(): void {
    // A pointer click on the backdrop moves focus outside the drawer before
    // Material can restore it, so return to the trigger for every dismissal.
    if (
      this.compactViewport() &&
      this.showSideNav &&
      this.compactNavigationTrigger?.isConnected
    ) {
      this.compactNavigationTrigger.focus({ preventScroll: true });
    }
  }

  protected onNavigationClick(event: MouseEvent): void {
    // Keep expanded groups open; selecting a destination dismisses the drawer.
    // Modified clicks may open a new tab without leaving the current page.
    if (
      this.compactViewport() &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.shiftKey &&
      !event.altKey &&
      event.button === 0 &&
      event.target instanceof Element &&
      event.target.closest('a[routerLink]')
    ) {
      this.compactNavigationOpen.set(false);
    }
  }

  public onRouterOutletActivate(event: unknown) {
    this.activatedComponentChanged.emit(event);
    this.showSideNav =
      !isScreenWithoutMenus(event) &&
      this.route.snapshot.queryParams['hideSideNav'] !== 'true';
    if (!this.showSideNav) {
      this.compactNavigationOpen.set(false);
    }
    this.cdr.markForCheck();
  }
}
